import React from 'react';
import { Dispatch } from 'redux';
import { connect } from "react-redux";
import { IApplicationState, RootActionCreators } from '../../../store';
import { createStyles, makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import { RouteComponentProps } from 'react-router';
import { IReportDataObject } from '../../../entities/reports/IReportData';
import { IData as IAutomaticTaskDetail } from '../../../store/automatictask/automaticTaskItem';
import { INavigations, mapNavigationsToProps, AutomaticTaskNavigation, AutomaticTaskNavigationPage } from '../../organisms/automatictask/AutomaticTaskNavigation';
import { PageHeader } from '../../organisms/PageHeader';
import { AutomaticTaskDetail } from '../../organisms/automatictask/AutomaticTaskDetail';
import { FetchState } from '../../../entities/FetchState';
import { RefreshButton } from '../../atoms/buttons/RefreshButton';
import { EditButton } from '../../atoms/buttons/EditButton';
import { DeleteButton } from '../../atoms/buttons/DeleteButton';
import { useTranslation } from 'react-i18next';
import ErrorMessage from '../../organisms/ErrorMessage';
import { DeletingMessage } from '../../organisms/DeletingMessage';

// CSS
const useStyles = makeStyles((theme: Theme) =>
    createStyles({}),
);

interface IMatchParams {
    id: string;
}
interface IPropsFromParams extends RouteComponentProps<IMatchParams> { }

// Standard Properties from parent component
interface IProps { }

// Outgoing functions to parent component
interface IDispatch {}

// Properties from State 
interface IStateProps {
  details: IReportDataObject<IAutomaticTaskDetail>;
  deleteState: IReportDataObject<IAutomaticTaskDetail>;
}
const mapStateProps = (state: IApplicationState): IStateProps => ({
    details: state.automatictask.automatictaskItem.Item,
    deleteState: state.automatictask.automatictaskItem.DeleteItem
});

// Outgoing functions to Dispatch
interface IDispatchProps {
    navigations: INavigations;
    onLoadData: (id: string) => void;
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
}
const mapDispatchProps = (dispatch: Dispatch): IDispatchProps => ({
    navigations: mapNavigationsToProps(dispatch),
    onLoadData: (id: string) => dispatch(RootActionCreators.Automatictask.automatictaskItem.FetchData(id)),
    onEdit: (id: string) => dispatch(RootActionCreators.Navigate.ToAutomaticTaskEdit(id)),
    onDelete: (id: string) => dispatch(RootActionCreators.Automatictask.automatictaskItem.DeleteData(id)),
});

// Actual Component
export type AllProps = IProps & IDispatch & IStateProps & IDispatchProps & IPropsFromParams;
export const AutomaticTaskDetails = (props: AllProps) => {
    const classes = useStyles(useTheme());
    const { t: tAutomaticTask } = useTranslation('automatictask');
    const { t: tCommon } = useTranslation('common');

    const loadData = () => props.onLoadData(props.match.params.id);

    React.useEffect(() => {
        const id = props.match.params.id;
        if (props.details.state === FetchState.NotFetched || (props.details.state === FetchState.Success && props.details.data.streamId !== id)) {
            props.onLoadData(id);
        }
    }, [props.match.params.id, props.details.state, props.details.data.streamId]);

    const loading = props.details.state !== FetchState.Success;
    const isDeleting = props.deleteState.state === FetchState.Busy;
    const hasDeleteError = props.deleteState.state === FetchState.Error;

    return <AutomaticTaskNavigation navigations={props.navigations} >
        <PageHeader
                title={tAutomaticTask('title')}
                subTitle={tAutomaticTask('detailTitle', { title: props.details.data.title })}
                explainText={tAutomaticTask('explainText')}
            />
        <AutomaticTaskDetail
          data={props.details.data}
          loading={loading}
          actions={<>
                <RefreshButton onClick={loadData} disabled={props.details.state === FetchState.Busy} />
                <EditButton onClick={() => props.onEdit(props.details.data.streamId)} disabled={loading} />
                <DeleteButton onClick={() => props.onDelete(props.details.data.streamId)} disabled={loading} deleteWarningMessage={tAutomaticTask('deleteWarning', { title: props.details.data.title })} />
            </>}
          />
        {props.details.state === FetchState.Error && <ErrorMessage errorDetails={props.details.errorMessage} message={tCommon('errorLoadingData')} title={tCommon('errorLoadingDataTitle')} options={[{optionLabel: tCommon('tryAgain'), optionExecute: loadData},{optionLabel: tCommon('restart'), optionExecute: () => window.location.reload()}]} />}
        
        {isDeleting && <DeletingMessage entity={tAutomaticTask('automaticTask')} />}
        {hasDeleteError && 
          <ErrorMessage 
            title={tCommon('deletingErrorTitle')} 
            message={tCommon('deletingErrorXMessage', {x: props.deleteState.errorMessage})} 
            canClose 
            options={[]} 
            errorDetails={props.deleteState.errorMessage} 
          />
        }
    </AutomaticTaskNavigation>;
};

export default connect<IStateProps, IDispatchProps, {}, IApplicationState>(mapStateProps, mapDispatchProps)(AutomaticTaskDetails);
